import { Button, Dialog, Grid, Paper, Typography } from '@mui/material';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { unitsApiKeyStore } from '../../../api/units-api-key-store';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { SiTrackPage } from '../../../components/si-track-page';
import { useLoadData } from '../../../hooks/useLoadData';
import { useSaveData } from '../../../hooks/useSaveData';
import { ApiResponse } from '../../../models/api-response';
import CountStatus from './count-status';
import { DetailsTabs } from './details-tabs';
import UnitInfo from './unit-Info';
import UnitKeyDetails from './unit-energy-star-details';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useAuthorize } from '../../../hooks/use-authorize';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { GuardedComponent } from '../../../components/guarded-component';
import { UnitProctorResultComponrnt } from './unit-proctor-result.component';

const styling = {
  paperStyle: { padding: '20px !imp', borderRadius: '10px' },
  countStyle: { color: '#0000b5', fontSize: 50 },
  iconImg: { margin: '0' },
};

export const UnitProfilePage = () => {
  const { unitID } = useParams();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);



  const { data: unitCount } = useLoadData<ApiResponse<any>>(unitsApiKeyStore.getUnitProfileDetails(unitID));
  const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any>>(
    unitsApiKeyStore.getUnitDetails(unitID)
  );

  const { isLoading: isLoadingUnitComplete, mutateAsync } = useSaveData<any>(unitsApiKeyStore.unitComplete());
  const { isLoading: isUnitDUXCocLoading, mutateAsync: DuxCocMutateAsync } = useSaveData<any>(
    unitsApiKeyStore.generateDoxCoc(unitID || 0)
  );

  const handleEditOffice = (value: any) => {
    navigate(`/units/edit/${unitID}`);
  };

  const closeDialog = () => {
    setOpenDialog(false);
  };

  const handleSumbitConfirm = async () => {
    const updatedData = {
      unitID: Number(unitID),
    };
    await mutateAsync({ data: updatedData });
    refetch();
    closeDialog();
  };

  const permissionForTab = {
    serviceTab: useAuthorize([UserPermissionName.VIEW_SERVICE_UNIT]),
    serviceBundleTab: useAuthorize([UserPermissionName?.VIEW_SERVICE_BUNDLE]),
    workorders: useAuthorize([UserPermissionName.VIEW_WORKORDER]),
    contactTab: useAuthorize([UserPermissionName.VIEW_CONTACT]),
    fileTab: useAuthorize([UserPermissionName.VIEW_FILES]),
    planTab: useAuthorize([UserPermissionName.VIEW_PLAN]),
    inspectionReportTab: useAuthorize([UserPermissionName.VIEW_WORKORDER])
  };
  const { showSnackbar } = useSnackbar();

  const handleGenerateCocClick = () => {
    if (data?.data?.municipalityID) {
      DuxCocMutateAsync({ data: {} })
        .then(() => {
          showSnackbar('Generated Successfully', 'success');
        })
        .catch(() => {
          showSnackbar('Failed to generate CoC', 'error');
        });
    } else {
      showSnackbar(
        'To generate a Certificate of Completion (COC), the lot must be mapped to a Municipality that has the COC template enabled.',
        'error'
      );
    }
  };

  const isGenerateCoc = useAuthorize([UserPermissionName?.GENERATE_COC]);
  const isDUXEnabled = process.env.REACT_APP_DUX_FLAG === 'true';

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <SiTrackPage
      title={data?.data?.unitName ?? ''}
      onEdit={!data?.data?.isCompleted ? handleEditOffice : undefined}
      isLoading={isLoading}
      actionPermissions={{ edit: UserPermissionName.EDIT_UNIT }}
    >
      {/* <CountStatus data={unitCount} /> */}

      <Grid display={'flex'} justifyContent={'flex-end'}>
        <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_UNIT]}>
          <Button
            type="button"
            disabled={isLoading || data?.data?.isCompleted || isReadOnlyPermission}
            variant="contained"
            style={{ width: '130px', marginBottom: 9 }}
            className="btn btn-sm btn-success"
            onClick={() => setOpenDialog(true)}
          >
            Complete
          </Button>
        </GuardedComponent>
        {isGenerateCoc && data?.data?.coCRequired && (
          <Button
            type="button"
            variant="contained"
            disabled={isReadOnlyPermission}
            style={{ marginBottom: 9, marginLeft: 10 }}
            className="btn btn-sm "
            onClick={handleGenerateCocClick}
          >
            {!data?.data?.coCRequested ? 'Generate CoC' : 'CoC Generation Requested'}
          </Button>
        )}
      </Grid>
      <LoadingIndicator isLoading={isUnitDUXCocLoading} />
      <Grid container spacing={2}>
        <Grid item xl={12} lg={12} sm={12} xs={12}>
          <UnitInfo data={data?.data} />
        </Grid>
        <Grid item xl={12} lg={12} sm={12} xs={12}>
          <UnitProctorResultComponrnt unitData={data?.data} />
        </Grid>
        {!permissionForTab?.serviceBundleTab &&
          !permissionForTab?.serviceTab &&
          !permissionForTab?.workorders &&
          !permissionForTab?.contactTab &&
          !permissionForTab?.fileTab &&
          !permissionForTab?.planTab &&
          !permissionForTab?.inspectionReportTab ? null : (
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            {data?.data && <DetailsTabs permissionForTab={permissionForTab} unit={data?.data} />}
          </Grid>
        )}
      </Grid>

      {openDialog && (
        <Dialog
          open={openDialog}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>
                This will move lot to a closed status and make it View Only. Please confirm all the work orders have
                been completed and payments received before completion.
              </div>
              <LoadingIndicator isLoading={isLoadingUnitComplete} />
            </div>
          </div>
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button
                style={{ cursor: 'pointer' }}
                className="btn btn-sm btn-default btn-lightGrey"
                onClick={closeDialog}
                autoFocus
              >
                Cancel
              </button>

              <button
                className="btn btn-sm btn-primary  btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer'
                }}
                type="button"
                onClick={handleSumbitConfirm}
                disabled={isLoading}
              >
                Confirm
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </SiTrackPage>
  );
};
